// Normalizes the composer's two reward catalogs (plain feedback vs
// recovery_ai) behind one hook, so `OfferPicker`/`InboxComposer` don't need
// to know which underlying query backs a given item's `kind`
// (docs/11-chassis-plan.md §2 PR 06 manifest: "wraps
// useGetGrantableRewardsQuery (ratings) / useGetAvailableRewardsQuery (RAI)
// with skip; normalizes to { id, name }[]"). Mirrors the reward-catalog
// consolidation note in parts/p06-composer.md's "BE notes" — both routes
// are backed by the same `Recovery::Configuration#grantable_make_good_offers`
// source, so this hook doesn't re-derive eligibility, only shape.
import type { FeedbackItemKind } from 'api/feedbackInbox.types'
import { useGetGrantableRewardsQuery } from 'api/orderRatings'
import { useGetAvailableRewardsQuery } from 'api/recoveryAi'
import { useMemo } from 'react'

export interface AvailableOffer {
  id: string
  name: string
}

export interface UseAvailableOffersResult {
  offers: AvailableOffer[]
  isLoading: boolean
  isError: boolean
}

// `id` (the feedback item's own id) isn't used to scope either underlying
// query today — both `order_ratings/available_rewards` and
// `recovery/available_rewards` return the merchant's whole catalog,
// unscoped by record — but it's kept in the signature per the manifest
// (`useAvailableOffers(kind, id)`) so a future per-record catalog doesn't
// require every call site to change shape.
export function useAvailableOffers(
  kind: FeedbackItemKind,
  _id: string
): UseAvailableOffersResult {
  const feedbackQuery = useGetGrantableRewardsQuery(undefined, {
    skip: kind !== 'feedback',
  })
  const recoveryQuery = useGetAvailableRewardsQuery(undefined, {
    skip: kind !== 'recovery_ai',
  })

  const query = kind === 'feedback' ? feedbackQuery : recoveryQuery

  // Dropped rather than rendered when a reward has no name or is inactive —
  // same rule GrantRewardModal applies to this catalog (an unnamed/inactive
  // template is nothing an operator can act on).
  // One pass rather than filter-then-map: TS can't narrow `label`
  // (`string | null`) across the two, which forced an `as string` on the
  // map side. `flatMap` keeps `name` a genuine `string` with no cast, and
  // stores the TRIMMED value — the filter previously tested `.trim()` but
  // stored the raw label, so a padded name kept its whitespace.
  const offers = useMemo<AvailableOffer[]>(
    () =>
      (query.data ?? []).flatMap(reward => {
        const name = reward.label?.trim()
        return reward.active && name ? [{ id: reward.id, name }] : []
      }),
    [query.data]
  )

  return {
    offers,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
